/**
 * Wallet Tracker - Smart Money Tracking
 * Track and analyze blockchain wallets in real-time
 * Modern redesign inspired by HederaMonitor and CronosMonitor
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWalletTrackerStore } from '../store/walletTrackerStore';
import { useAlertStore } from '../store/alertStore';
import { useAuthStore } from '../store/authStore';
import { useAlertMonitor } from '../hooks/wallet-tracker/useAlertMonitor';
import { AddWalletModal } from '../components/wallet-tracker/AddWalletModal';
import { WalletList } from '../components/wallet-tracker/WalletList';
import { WalletDetails } from './WalletDetails';
import { AlertList } from '../components/wallet-tracker/AlertList';
import { NotificationPanel } from '../components/wallet-tracker/NotificationPanel';
import { Icons } from '../components/Icons';

// ============================================
// CONSTANTS & STYLES
// ============================================

const TRACKER_COLORS = {
  primary: '#06b6d4', // Cyan
  secondary: '#0891b2', // Darker cyan
  accent: '#22d3ee', // Light cyan
  gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)',
  lightGradient: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(8, 145, 178, 0.1))',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  purple: '#8b5cf6',
};

type TabType = 'wallets' | 'alerts';

const s = {
  page: {
    padding: '0',
    maxWidth: '1400px',
    margin: '0 auto',
    paddingBottom: '40px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    gap: '20px',
    flexWrap: 'wrap' as const,
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  titleIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    background: TRACKER_COLORS.gradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleText: {
    fontSize: '24px',
    fontWeight: 700,
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: 'var(--muted)',
    marginTop: '4px',
  },
  card: {
    background: 'rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    padding: '20px',
    marginBottom: '20px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: (color: string) => ({
    background: `${color}10`,
    border: `1px solid ${color}30`,
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'center' as const,
  }),
  statValue: {
    fontSize: '22px',
    fontWeight: 700,
  },
  statLabel: {
    fontSize: '11px',
    color: 'var(--muted)',
    marginTop: '4px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  button: {
    padding: '12px 20px',
    background: TRACKER_COLORS.gradient,
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  },
  buttonSecondary: {
    padding: '10px 16px',
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'var(--text)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
    position: 'relative' as const,
  },
  tabContainer: {
    display: 'flex',
    gap: '4px',
    padding: '4px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '14px',
    width: 'fit-content',
    marginBottom: '24px',
  },
  tab: (isActive: boolean) => ({
    padding: '10px 18px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.3s ease',
    background: isActive ? TRACKER_COLORS.gradient : 'transparent',
    color: isActive ? 'white' : 'var(--muted)',
    transform: isActive ? 'scale(1.02)' : 'scale(1)',
    boxShadow: isActive ? '0 4px 15px rgba(6, 182, 212, 0.3)' : 'none',
  }),
  emptyState: {
    textAlign: 'center' as const,
    padding: '60px 20px',
  },
  badge: (color: string) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    padding: '0 6px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 700,
    background: color,
    color: 'white',
  }),
};

// ============================================
// SUB-COMPONENTS
// ============================================

/**
 * Network Stats Banner
 */
function NetworkStatsBanner({ walletsCount, alertsCount }: {
  walletsCount: number;
  alertsCount: number;
}) {
  const { t } = useTranslation();

  return (
    <div style={{
      ...s.card,
      background: `linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(8, 145, 178, 0.1))`,
      border: '1px solid rgba(6, 182, 212, 0.25)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('walletTracker.network.title', 'Smart Money Tracker')}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 700, color: TRACKER_COLORS.primary }}>
              {walletsCount}
            </span>
            <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
              {t('walletTracker.network.walletsTracked', 'wallets tracked')}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600, color: TRACKER_COLORS.success }}>
              <Icons.Activity size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
              Live
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('walletTracker.network.monitoring', 'Monitoring')}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>
              Ethereum
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('walletTracker.network.network', 'Network')}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: TRACKER_COLORS.success,
              boxShadow: `0 0 10px ${TRACKER_COLORS.success}`,
              margin: '0 auto 4px',
              animation: 'pulse 2s infinite',
            }} />
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('walletTracker.network.connected', 'Connected')}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 10px ${TRACKER_COLORS.success}; }
          50% { opacity: 0.6; box-shadow: 0 0 20px ${TRACKER_COLORS.success}; }
        }
      `}</style>
    </div>
  );
}

/**
 * Stats Overview Cards
 */
function StatsOverview({ walletsCount, alertsCount, unreadCount }: {
  walletsCount: number;
  alertsCount: number;
  unreadCount: number;
}) {
  const { t } = useTranslation();

  const stats = [
    {
      label: t('walletTracker.stats.wallets', 'Wallets'),
      value: walletsCount.toString(),
      subvalue: t('walletTracker.stats.tracking', 'tracking'),
      color: TRACKER_COLORS.primary,
      icon: <Icons.Wallet size={16} />,
    },
    {
      label: t('walletTracker.stats.alerts', 'Alerts'),
      value: alertsCount.toString(),
      subvalue: t('walletTracker.stats.configured', 'configured'),
      color: TRACKER_COLORS.purple,
      icon: <Icons.Bell size={16} />,
    },
    {
      label: t('walletTracker.stats.unread', 'Unread'),
      value: unreadCount.toString(),
      subvalue: t('walletTracker.stats.notifications', 'notifications'),
      color: unreadCount > 0 ? TRACKER_COLORS.error : TRACKER_COLORS.success,
      icon: <Icons.AlertCircle size={16} />,
    },
    {
      label: t('walletTracker.stats.network', 'Network'),
      value: 'ETH',
      subvalue: 'Mainnet',
      color: TRACKER_COLORS.secondary,
      icon: <Icons.Globe size={16} />,
    },
  ];

  return (
    <div style={s.statsGrid}>
      {stats.map((stat, idx) => (
        <div key={idx} style={s.statCard(stat.color)}>
          <div style={{ color: stat.color, marginBottom: '8px' }}>{stat.icon}</div>
          <div style={{ ...s.statValue, color: stat.color }}>{stat.value}</div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>{stat.subvalue}</div>
          <div style={s.statLabel}>{stat.label}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Empty State Component
 */
function EmptyState({ onAddWallet }: { onAddWallet: () => void }) {
  const { t } = useTranslation();

  const features = [
    {
      icon: <Icons.Activity size={28} />,
      title: t('walletTracker.features.realTimeTransactions.title', 'Real-time Tracking'),
      desc: t('walletTracker.features.realTimeTransactions.description', 'Monitor transactions live'),
    },
    {
      icon: <Icons.PieChart size={28} />,
      title: t('walletTracker.features.portfolioAnalysis.title', 'Portfolio Analysis'),
      desc: t('walletTracker.features.portfolioAnalysis.description', 'Track holdings & value'),
    },
    {
      icon: <Icons.TrendingUp size={28} />,
      title: t('walletTracker.features.smartMoneyInsights.title', 'Smart Money'),
      desc: t('walletTracker.features.smartMoneyInsights.description', 'Follow whale movements'),
    },
    {
      icon: <Icons.Bell size={28} />,
      title: t('walletTracker.features.alerts', 'Custom Alerts'),
      desc: t('walletTracker.features.alertsDesc', 'Get notified on activity'),
    },
  ];

  return (
    <div style={{
      ...s.card,
      ...s.emptyState,
      background: `linear-gradient(135deg, rgba(6, 182, 212, 0.08), rgba(8, 145, 178, 0.05))`,
      border: '1px solid rgba(6, 182, 212, 0.2)',
    }}>
      <div style={{
        width: '80px',
        height: '80px',
        borderRadius: '20px',
        background: TRACKER_COLORS.gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
        boxShadow: '0 8px 32px rgba(6, 182, 212, 0.3)',
      }}>
        <Icons.Eye size={40} color="white" />
      </div>
      <h3 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '8px' }}>
        {t('walletTracker.emptyState.title', 'Start Tracking Smart Money')}
      </h3>
      <p style={{ color: 'var(--muted)', marginBottom: '32px', maxWidth: '450px', margin: '0 auto 32px', lineHeight: 1.6 }}>
        {t('walletTracker.emptyState.description', 'Add wallet addresses to monitor transactions, analyze portfolios, and get alerts on whale movements.')}
      </p>

      {/* Features Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '16px',
        maxWidth: '600px',
        margin: '0 auto 32px',
      }}>
        {features.map((feature, idx) => (
          <div key={idx} style={{
            padding: '20px 16px',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.06)',
          }}>
            <div style={{ color: TRACKER_COLORS.primary, marginBottom: '12px' }}>{feature.icon}</div>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>{feature.title}</div>
            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{feature.desc}</div>
          </div>
        ))}
      </div>

      {/* CTA Button */}
      <button
        onClick={onAddWallet}
        style={{
          ...s.button,
          padding: '14px 32px',
          fontSize: '15px',
          margin: '0 auto',
        }}
      >
        <Icons.Plus size={20} />
        {t('walletTracker.emptyState.addFirstWallet', 'Add Your First Wallet')}
      </button>

      {/* Tip */}
      <div style={{
        marginTop: '40px',
        paddingTop: '24px',
        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
      }}>
        <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '4px' }}>
          💡 {t('walletTracker.emptyState.tip', 'Pro tip: Track known whale wallets to follow smart money movements')}
        </p>
        <p style={{ fontSize: '11px', color: 'var(--muted)', opacity: 0.7 }}>
          {t('walletTracker.emptyState.tipSubtitle', 'Popular wallets are often shared on Crypto Twitter and Etherscan')}
        </p>
      </div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function WalletTracker() {
  const { t } = useTranslation();
  const { wallets } = useWalletTrackerStore();
  const { alerts, getUnreadCount, showNotifications, setShowNotifications } = useAlertStore();
  const { isAuthenticated } = useAuthStore();
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedWalletAddress, setSelectedWalletAddress] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('wallets');

  const unreadCount = getUnreadCount();
  const alertsCount = alerts?.length || 0;

  // Monitor transactions and trigger alerts
  useAlertMonitor();

  // Not logged in - require authentication
  if (!isAuthenticated) {
    return (
      <div style={s.page}>
        <div style={s.header}>
          <div>
            <div style={s.title}>
              <div style={s.titleIcon}>
                <Icons.Eye size={20} color="white" />
              </div>
              <h1 style={s.titleText}>{t('walletTracker.title', 'Wallet Tracker')}</h1>
            </div>
            <p style={s.subtitle}>
              {t('walletTracker.pageDescription', 'Track and analyze blockchain wallets in real-time')}
            </p>
          </div>
        </div>

        <div style={{
          ...s.card,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          textAlign: 'center',
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '16px',
            background: 'rgba(139, 92, 246, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px',
          }}>
            <Icons.Lock size={32} color="#8b5cf6" />
          </div>
          <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>
            {t('walletTracker.loginRequired', 'Connexion requise')}
          </h2>
          <p style={{ color: 'var(--muted)', fontSize: '14px', maxWidth: '300px' }}>
            {t('walletTracker.loginMessage', 'Connectez-vous pour accéder au Wallet Tracker et suivre vos wallets.')}
          </p>
        </div>
      </div>
    );
  }

  const handleOpenModal = () => {
    setShowAddModal(true);
  };

  // Tabs configuration
  const tabs: { id: TabType; label: string; icon: React.ReactNode; badge?: number }[] = [
    {
      id: 'wallets',
      label: t('walletTracker.tabs.wallets', 'Wallets'),
      icon: <Icons.Wallet size={16} />,
      badge: wallets.length,
    },
    {
      id: 'alerts',
      label: t('walletTracker.tabs.alerts', 'Alerts'),
      icon: <Icons.Bell size={16} />,
      badge: unreadCount > 0 ? unreadCount : undefined,
    },
  ];

  // Show wallet details if one is selected
  if (selectedWalletAddress) {
    return (
      <WalletDetails
        address={selectedWalletAddress}
        onBack={() => setSelectedWalletAddress(null)}
      />
    );
  }

  // Empty State - No wallets tracked yet
  if (wallets.length === 0) {
    return (
      <div style={s.page}>
        {/* Header */}
        <div style={s.header}>
          <div>
            <div style={s.title}>
              <div style={s.titleIcon}>
                <Icons.Eye size={20} color="white" />
              </div>
              <h1 style={s.titleText}>{t('walletTracker.title', 'Wallet Tracker')}</h1>
            </div>
            <p style={s.subtitle}>
              {t('walletTracker.pageDescription', 'Track and analyze blockchain wallets in real-time')}
            </p>
          </div>
          {/* Add Wallet Button */}
          <button
            onClick={handleOpenModal}
            style={s.button}
          >
            <Icons.Plus size={16} />
            {t('walletTracker.header.addWallet', 'Add Wallet')}
          </button>
        </div>

        {/* Network Banner even in empty state */}
        <NetworkStatsBanner walletsCount={0} alertsCount={0} />

        {/* Empty State */}
        <EmptyState onAddWallet={handleOpenModal} />

        {/* Add Wallet Modal */}
        <AddWalletModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
        />
      </div>
    );
  }

  // Wallet List View
  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <div style={s.title}>
            <div style={s.titleIcon}>
              <Icons.Eye size={20} color="white" />
            </div>
            <h1 style={s.titleText}>{t('walletTracker.title', 'Wallet Tracker')}</h1>
          </div>
          <p style={s.subtitle}>
            {t('walletTracker.header.walletsTracked', '{{count}} wallets tracked', { count: wallets.length })} • {t('walletTracker.header.ethereumNetwork', 'Ethereum Network')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* Notification Bell */}
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            style={{
              ...s.buttonSecondary,
              padding: '10px',
            }}
            title={t('walletTracker.header.notifications', 'Notifications')}
          >
            <Icons.Bell size={18} />
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                ...s.badge(TRACKER_COLORS.error),
                minWidth: '18px',
                height: '18px',
                fontSize: '10px',
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Add Wallet Button */}
          <button
            onClick={handleOpenModal}
            style={s.button}
          >
            <Icons.Plus size={16} />
            {t('walletTracker.header.addWallet', 'Add Wallet')}
          </button>
        </div>
      </div>

      {/* Network Stats Banner */}
      <NetworkStatsBanner walletsCount={wallets.length} alertsCount={alertsCount} />

      {/* Stats Overview */}
      <StatsOverview
        walletsCount={wallets.length}
        alertsCount={alertsCount}
        unreadCount={unreadCount}
      />

      {/* Tabs */}
      <div style={s.tabContainer}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={s.tab(activeTab === tab.id)}
          >
            {tab.icon}
            {tab.label}
            {tab.badge !== undefined && (
              <span style={{
                ...s.badge(activeTab === tab.id ? 'rgba(255,255,255,0.3)' : (tab.id === 'alerts' && unreadCount > 0 ? TRACKER_COLORS.error : 'rgba(255,255,255,0.15)')),
                marginLeft: '4px',
              }}>
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{
        ...s.card,
        padding: 0,
        overflow: 'hidden',
      }}>
        {activeTab === 'wallets' ? (
          <WalletList
            onSelectWallet={(address) => {
              setSelectedWalletAddress(address);
            }}
          />
        ) : (
          <AlertList />
        )}
      </div>

      {/* Notification Panel */}
      <NotificationPanel />

      {/* Add Wallet Modal */}
      <AddWalletModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />

      {/* Info Footer */}
      <div style={{
        marginTop: '20px',
        padding: '16px 20px',
        background: 'rgba(6, 182, 212, 0.08)',
        borderRadius: '12px',
        border: '1px solid rgba(6, 182, 212, 0.15)',
        fontSize: '12px',
        color: 'var(--muted)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <Icons.Info size={14} style={{ color: TRACKER_COLORS.primary }} />
          <strong style={{ color: 'var(--text)' }}>{t('walletTracker.info.title', 'About Wallet Tracker')}</strong>
        </div>
        <p style={{ lineHeight: 1.6 }}>
          {t('walletTracker.info.description', 'Track any Ethereum wallet address to monitor transactions, analyze portfolio composition, and set up custom alerts. Data is fetched from public blockchain explorers in real-time.')}
        </p>
      </div>
    </div>
  );
}
